import {useEffect, useState} from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  Dropdown,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {useNavigate} from 'react-router-dom'
import {FormattedMessage, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {GET_ALL_PDF_DOCUMENT_TEMPLATES} from '../../gql/queries/pdfEnvelopesApiQueries'
import {UPDATE_PDF_DOCUMENT_TEMPLATE, DELETE_PDF_DOCUMENT_TEMPLATE} from '../../gql/mutations/pdfEnvelopesMutations'
import {IResAllPdfDocumentTemplates, NodePdfDocumentTemplate} from '../../types/pdfEnvelopesApi'
import {parseId, hasPermission} from '../../helpers'
import {ALL_PERMISSIONS, SIZE_PAGE_DEFAULT} from '../../helpers/consts'
import {useGetUser} from '../../store/userStore'
import {useDisclourse} from '../../hooks/useDisclourse'
import {useLocalStorage} from '../../hooks/useLocalStorage'
import {PaginationUtils} from '../shared/PaginationUtils'
import {PaginationSection} from '../shared/PaginationSection'
import moment from 'moment'
import {BadgeStatus} from './components/BadgeStatus'
import {ModalAddPdfTemplate} from './components/ModalAddPdfTemplate'
import {ModalPdfFieldEditor} from './components/ModalPdfFieldEditor'
import {ModalEnvelopePreview} from './components/ModalEnvelopePreview'
import {ModalRequestEnvelope} from './components/ModalRequestEnvelope'

function parseJsonLen(val: any): number {
  if (!val) return 0
  if (Array.isArray(val)) return val.length
  if (typeof val === 'string') {
    try { return JSON.parse(val).length } catch { return 0 }
  }
  return 0
}

const emDash = '—'

type IFilterStatus = 'all' | 'DRAFT' | 'ACTIVE' | 'INACTIVE'

/**
 * Los filtros se guardan en localStorage: la lista se remonta al volver de
 * "View envelopes" o del editor, y perder el texto de búsqueda obligaba a
 * reescribirlo en cada ida y vuelta.
 */
const PDF_TEMPLATE_FILTER_KEYS = {
  name: 'pdf_templates_filter_name',
  status: 'pdf_templates_filter_status',
  pageSize: 'pdf_templates_filter_page_size',
} as const

export const PdfEnvelopesPage = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const permissions = useGetUser((s) => s.user?.permissions)
  const canAdd = permissions && hasPermission(permissions, ALL_PERMISSIONS.ADD_PDF_ENVELOPES)
  const canEdit = permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_PDF_ENVELOPES)

  const [gqlGetTemplates, {data, loading, error, refetch}] =
    useLazyQuery<IResAllPdfDocumentTemplates>(GET_ALL_PDF_DOCUMENT_TEMPLATES, {
      fetchPolicy: 'no-cache',
    })

  const [updateTemplate] = useMutation(UPDATE_PDF_DOCUMENT_TEMPLATE)
  const [deleteTemplate] = useMutation(DELETE_PDF_DOCUMENT_TEMPLATE)

  const {isOpen: isOpenAdd, onOpen: onOpenAdd, onClose: onCloseAdd} = useDisclourse()
  const {isOpen: isOpenEditor, onOpen: onOpenEditor, onClose: onCloseEditor} = useDisclourse()
  const {isOpen: isOpenPreview, onOpen: onOpenPreview, onClose: onClosePreview} = useDisclourse()
  const {isOpen: isOpenRequest, onOpen: onOpenRequest, onClose: onCloseRequest} = useDisclourse()

  const [selectedTemplate, setSelectedTemplate] = useState<NodePdfDocumentTemplate | null>(null)
  const [selectedEnvelopeId] = useState<string>('')
  const [nameFilter, setNameFilter] = useLocalStorage(PDF_TEMPLATE_FILTER_KEYS.name, '')
  const [filterStatus, setFilterStatus] = useLocalStorage<IFilterStatus>(
    PDF_TEMPLATE_FILTER_KEYS.status,
    'all'
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useLocalStorage(PDF_TEMPLATE_FILTER_KEYS.pageSize, SIZE_PAGE_DEFAULT)
  const [dataArr, setDataArr] = useState<{node: NodePdfDocumentTemplate}[]>([])

  const total = data?.allPdfDocumentTemplates?.totalCount ?? 0
  const totalPages = Math.ceil(total / first)
  const {start, end} = PaginationUtils.getPaginationRange(currentPage, first, total)

  const loadTemplates = () => {
    gqlGetTemplates({
      variables: {
        first,
        offset: (currentPage - 1) * first,
        name: nameFilter || null,
        status: filterStatus === 'all' ? null : filterStatus,
      },
    })
  }

  useEffect(() => {
    loadTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first, currentPage])

  useEffect(() => {
    if (data) setDataArr(data.allPdfDocumentTemplates.edges)
  }, [data])

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: error.message}
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleStatusChange = async (id: string, status: string) => {
    try {
      const {data} = await updateTemplate({variables: {id: parseId(id), status}})
      if (data?.pdfTemplateUpdate?.ok) {
        toast.success(
          status === 'ACTIVE'
            ? intl.formatMessage({
                id: 'documents.pdfEnvelopes.page.toast.markedActive',
                defaultMessage: 'Template marked as active',
              })
            : intl.formatMessage({
                id: 'documents.pdfEnvelopes.page.toast.markedInactive',
                defaultMessage: 'Template marked as inactive',
              })
        )
        refetch()
      } else {
        toast.error(
          data?.pdfTemplateUpdate?.errors?.[0]?.message ||
            intl.formatMessage({
              id: 'documents.pdfEnvelopes.page.toast.updateError',
              defaultMessage: 'Error updating',
            })
        )
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  const handleDelete = async (id: string, name: string) => {
    const confirmMsg = intl.formatMessage(
      {id: 'documents.pdfEnvelopes.page.confirmDelete', defaultMessage: 'Delete template "{name}"?'},
      {name}
    )
    if (!window.confirm(confirmMsg)) return
    try {
      const {data} = await deleteTemplate({variables: {id: parseId(id)}})
      if (data?.pdfTemplateDelete?.ok) {
        toast.success(
          intl.formatMessage({
            id: 'documents.pdfEnvelopes.page.toast.deleted',
            defaultMessage: 'Template deleted',
          })
        )
        refetch()
      } else {
        toast.error(
          data?.pdfTemplateDelete?.errors?.[0]?.message ||
            intl.formatMessage({
              id: 'documents.pdfEnvelopes.page.toast.deleteError',
              defaultMessage: 'Error deleting',
            })
        )
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <div className='signatureForms-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>
            <FormattedMessage
              id='documents.pdfEnvelopes.page.breadcrumb'
              defaultMessage='PDF signing'
            />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          {canAdd && (
            <Button size='sm' onClick={onOpenAdd}>
              <i className='bi bi-plus fs-3' /> <FormattedMessage
                id='documents.pdfEnvelopes.page.newTemplate'
                defaultMessage='New PDF template'
              />
            </Button>
          )}
        </div>
      </header>

      <Row className='signatureForms'>
        <Col className='container'>
          <div className='signatureForms-container'>
            <div className='title-header'>
              <h3>
                <FormattedMessage
                  id='documents.pdfEnvelopes.page.title'
                  defaultMessage='PDF document templates'
                />
              </h3>
            </div>

            <div className='filters-header'>
              <InputGroup>
                <Form.Control
                  placeholder={intl.formatMessage({
                    id: 'documents.pdfEnvelopes.page.searchPlaceholder',
                    defaultMessage: 'Search by name...',
                  })}
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') loadTemplates()
                  }}
                />
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as IFilterStatus)}
                >
                  <option value='all'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopes.page.status.all', defaultMessage: 'All statuses'})}
                  </option>
                  <option value='DRAFT'>
                    {intl.formatMessage({id: 'documents.pdfEnvelopes.page.status.draft', defaultMessage: 'Draft'})}
                  </option>
                  <option value='ACTIVE'>
                    {intl.formatMessage({id: 'common.status.active', defaultMessage: 'Active'})}
                  </option>
                  <option value='INACTIVE'>
                    {intl.formatMessage({id: 'common.status.inactive', defaultMessage: 'Inactive'})}
                  </option>
                </Form.Select>
                <Button variant='outline-success' style={{border: '1px solid #dbdfe9'}} onClick={loadTemplates}>
                  <FormattedMessage id='common.action.search' defaultMessage='Search' />
                </Button>
              </InputGroup>
            </div>

            <div className='results-header'>
              <PaginationSection
                first={first}
                setFirst={(n) => {setFirst(n); setCurrentPage(1)}}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={total}
                updatePage={(n) => setCurrentPage(n)}
                PaginationUtils={PaginationUtils}
              />
            </div>

            <div className='table-responsive table table-striped table-hover'>
              <table className='table-scrollable table-scroll table table-striped table-hover' style={{whiteSpace: 'nowrap'}}>
                <thead>
                  <tr>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.name' defaultMessage='Name' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.status' defaultMessage='Status' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopes.editor.roles' defaultMessage='Roles' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopes.page.col.fields' defaultMessage='Fields' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='documents.pdfEnvelopes.page.col.envelopes' defaultMessage='Envelopes' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.created' defaultMessage='Created' />
                    </th>
                    <th className='fw-bold'>
                      <FormattedMessage id='common.field.actions' defaultMessage='Actions' />
                    </th>
                  </tr>
                </thead>
                {loading ? (
                  <tbody>
                    <tr>
                      <td colSpan={7}>
                        <div className='d-flex flex-center' style={{height: '30vh'}}>
                          <Spinner animation='border' />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                ) : (
                  <tbody>
                    {dataArr.length === 0 && (
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex flex-center' style={{height: '20vh'}}>
                            <FormattedMessage
                              id='documents.pdfEnvelopes.page.empty'
                              defaultMessage='No PDF templates found. Create one.'
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {dataArr.map(({node}) => (
                      <tr key={node.id}>
                        <td>
                          <span
                            className='text-primary'
                            style={{cursor: 'pointer'}}
                            onClick={() => {
                              setSelectedTemplate(node)
                              onOpenEditor()
                            }}
                          >
                            {node.name}
                          </span>
                        </td>
                        <td><BadgeStatus state={node.status} /></td>
                        <td>{parseJsonLen(node.signerRoles)}</td>
                        <td>{parseJsonLen(node.fieldDefinitions)}</td>
                        <td>{node.envelopesCount ?? 0}</td>
                        <td>{node.created ? moment(node.created).format('DD MMM, YY') : emDash}</td>
                        <td>
                          <Dropdown as={ButtonGroup}>
                            <Button
                              variant='outline-primary'
                              size='sm'
                              style={{border: '1px solid #3e97ff'}}
                              onClick={() => {
                                setSelectedTemplate(node)
                                onOpenEditor()
                              }}
                            >
                              <FormattedMessage
                                id='documents.pdfEnvelopes.page.editor'
                                defaultMessage='Editor'
                              />
                            </Button>
                            <Dropdown.Toggle
                              split
                              variant='outline-primary'
                              size='sm'
                              id={`dd-${node.id}`}
                              style={{border: '1px solid #3e97ff'}}
                            />
                            <Dropdown.Menu>
                              {node.status === 'ACTIVE' && (
                                <Dropdown.Item
                                  onClick={() => {
                                    setSelectedTemplate(node)
                                    onOpenRequest()
                                  }}
                                >
                                  <i className='bi bi-send-fill' /> <FormattedMessage
                                    id='documents.pdfEnvelopes.page.sendForSignature'
                                    defaultMessage='Send for signature'
                                  />
                                </Dropdown.Item>
                              )}
                              {canEdit && node.status !== 'ACTIVE' && (
                                <Dropdown.Item onClick={() => handleStatusChange(node.id, 'ACTIVE')}>
                                  <i className='bi bi-check-square-fill' /> <FormattedMessage
                                    id='documents.pdfEnvelopes.page.activate'
                                    defaultMessage='Activate'
                                  />
                                </Dropdown.Item>
                              )}
                              {canEdit && node.status === 'ACTIVE' && (
                                <Dropdown.Item onClick={() => handleStatusChange(node.id, 'INACTIVE')}>
                                  <i className='bi bi-folder-fill' /> <FormattedMessage
                                    id='documents.pdfEnvelopes.page.deactivate'
                                    defaultMessage='Deactivate'
                                  />
                                </Dropdown.Item>
                              )}
                              <Dropdown.Item
                                onClick={() => navigate(`/pdf-envelopes/${parseId(node.id)}/envelopes`)}
                              >
                                <i className='bi bi-list-check' /> <FormattedMessage
                                  id='documents.pdfEnvelopes.page.viewEnvelopes'
                                  defaultMessage='View envelopes'
                                />
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item
                                className='text-danger'
                                disabled={(node.envelopesCount ?? 0) > 0}
                                title={
                                  (node.envelopesCount ?? 0) > 0
                                    ? 'Cannot delete: this template has envelopes'
                                    : undefined
                                }
                                onClick={() => handleDelete(node.id, node.name)}
                              >
                                <i className='bi bi-trash' /> <FormattedMessage
                                  id='common.action.delete'
                                  defaultMessage='Delete'
                                />
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </Col>
      </Row>

      {isOpenAdd && (
        <ModalAddPdfTemplate
          isOpen={isOpenAdd}
          onClose={onCloseAdd}
          refetch={refetch}
          onTemplateCreated={(id) => {
            // After creation, open editor for field placement
            const newTemplate = data?.allPdfDocumentTemplates?.edges?.find(
              (e) => parseId(e.node.id) === id
            )?.node
            if (newTemplate) {
              setSelectedTemplate(newTemplate)
              onOpenEditor()
            }
          }}
        />
      )}

      {isOpenEditor && selectedTemplate && (
        <ModalPdfFieldEditor
          template={selectedTemplate}
          isOpen={isOpenEditor}
          onClose={onCloseEditor}
          refetch={refetch}
        />
      )}

      {isOpenPreview && selectedEnvelopeId && (
        <ModalEnvelopePreview
          envelopeId={selectedEnvelopeId}
          isOpen={isOpenPreview}
          onClose={onClosePreview}
          refetch={refetch}
        />
      )}

      {isOpenRequest && (
        <ModalRequestEnvelope
          isOpen={isOpenRequest}
          onClose={onCloseRequest}
          preSelectedTemplateId={selectedTemplate?.id}
          refetch={refetch}
        />
      )}
    </div>
  )
}
